import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Isvg from 'react-inlinesvg';
import Notification from 'app/components/general/Notification';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { ButtonGroup } from 'reactstrap';
import history from 'app/routes/history';
import _ from 'lodash';

import SectionGroup from 'app/components/general/SectionGroup';

import { ITeamsSelector, IPaymentEventsSelector } from 'app/types/selector';
import * as paymentEventsActions from 'app/ducks/paymentEvents/actions';
import * as teamPaymentRequestsActions from 'app/ducks/dashboardTeamPaymentRequests/actions';

import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as paymentEventsSelectors from 'app/ducks/paymentEvents/selectors';
import * as teamsSelectors from 'app/ducks/teams/selectors';
import * as organisationsSelectors from 'app/ducks/organisations/selectors';
import * as teamPaymentRequestsSelectors from 'app/ducks/dashboardTeamPaymentRequests/selectors';

import PaymentCardMin from 'app/components/matches/PaymentCardMin';
import Button from 'app/components/general/Button';

import ArrowRightIcon from 'app/images/icons/arrow-right.svg';

function selector(state): any {
  return {
    appReady: loadingSelectors.appReady(state),
    paymentEvents: paymentEventsSelectors.getPaymentEvents(state),
    teams: teamsSelectors.getTeams(state),
    teamPaymentRequests: teamPaymentRequestsSelectors.getTeamPaymentRequests(state),
    currentOrganisation: organisationsSelectors.getCurrentOrganisation(state),
    isLoading: state.payments.isLoading,
    errors: { ...state.paymentEvents.backendErrors },
  };
}

const actionCreators = _.merge(paymentEventsActions, teamPaymentRequestsActions);

interface IProps {
  teamId: string;
  groupIds: string[];
  matchPaymentIds: string[];
  orgIds: string[];
  appReady: any;
  teams: ITeamsSelector;
  paymentEvents: IPaymentEventsSelector;
  teamPaymentRequests: any;
  actions: any;
  dashboard: any;
  currentOrganisation: any;
  isLoading: boolean;
  multipleTeams: boolean;
  errors: any;
}

interface IState {
  numberOfCards: number;
}

const DashboardUpcomingMatches: FC<IProps> = (props: IProps = {
  teamId: undefined,
  groupIds: [],
  paymentEvents: {},
  teams: {},
  actions: undefined,
  dashboard: {},
  teamPaymentRequests: {},
  currentOrganisation: {},
  isLoading: false,
  appReady: false,
  multipleTeams: false,
  errors: {},
}) => {
  const [numberOfCards, setNumberOfCards] = useState(4);
  const [paymentStatus, setPaymentStatus] = useState('upcoming');
  const [connectionNotification, setConnectionNotification] = useState(true);

  useEffect(() => {
    if (!_.isEmpty(props.groupIds)) {
      props.actions.getAllPaymentEvents(
        {
          filter: {
            'group-id': props.groupIds.join(','),
          },
          sort: '-match-at',
          page: {
            size: 200,
          },
        },
        false,
        _.uniq(props.orgIds).join(','),
      );
    }
  }, [JSON.stringify(props.groupIds)]);

  useEffect(() => {
    if (!_.isEmpty(props.paymentEvents.paymentEventIds)) {
      props.actions.getAllDashboardTeamPaymentRequests(
        {
          filter: {
            'match-payment-forfeit-id': _.uniq(_.flatMap(props.paymentEvents.paymentEventsById, (p) => {
              const returnIds = [];
              if (_.includes(props.groupIds, p.groupOne.id)) {
                returnIds.push(p.groupOneMatchPayment.id);
                returnIds.push(p.groupOneForfeit.id);
              }
              if (_.includes(props.groupIds, p.groupTwo.id)) {
                returnIds.push(p.groupTwoMatchPayment.id);
                returnIds.push(p.groupTwoForfeit.id);
              }
              return returnIds;
            })).join(','),
          },
          page: {
            size: 200,
          },
        },
        false,
      );
    }
  }, [JSON.stringify(props.paymentEvents.paymentEventsById)]);

  const matchCards: {
    upcoming: any[];
    overdue: any[];
    paid: any[];
  } = useMemo(() => {
    const paymentEventsByIdAndGroupId: {
      [key: string]: {
        paymentEventId: string;
        groupId: string;
      };
    } = {};
    _.map(props.paymentEvents.paymentEventsById, (paymentEvent) => {
      if (_.includes(props.groupIds, paymentEvent.groupOne.id)) {
        paymentEventsByIdAndGroupId[`${paymentEvent.id}-1`] = {
          paymentEventId: paymentEvent.id,
          groupId: paymentEvent.groupOne.id,
        };
      }
      if (_.includes(props.groupIds, paymentEvent.groupTwo.id)) {
        paymentEventsByIdAndGroupId[`${paymentEvent.id}-2`] = {
          paymentEventId: paymentEvent.id,
          groupId: paymentEvent.groupTwo.id,
        };
      }
    });
    const paymentEvents = paymentEventsSelectors.getPaymentEventsByIdSortedStatus(paymentEventsByIdAndGroupId, props.paymentEvents.paymentEventsById, props.groupIds);
    return paymentEvents;
  }, [JSON.stringify(props.paymentEvents.paymentEventsById), JSON.stringify(props.groupIds)]);

  useEffect(() => {
    if (matchCards.upcoming.length === 0 && matchCards.overdue.length > 0) {
      setPaymentStatus('overdue');
    } else if (matchCards.upcoming.length === 0 && matchCards.paid.length > 0) {
      setPaymentStatus('paid');
    }
  }, [matchCards]);

  const noConnectedTeams = (): boolean => {
    const groupIds = _.filter(
      props.teams.teamsById,
      (team) => {
        if (team.hasTeamConnection) {
          return true;
        }
      },
    );
    return props.teams.hasLoaded && _.isEmpty(groupIds);
  };
  const noActiveTeams = (): boolean => {
    const groupIds = _.filter(
      props.teams.teamsById,
      (team) => {
        if (team.hasActiveSeasonConnection) {
          return true;
        }
      },
    );
    return props.teams.hasLoaded && _.isEmpty(groupIds);
  };

  useEffect(() => {
    if (props.teams.hasLoaded && !noConnectedTeams() && props.dashboard.hideUpcomingMatches) {
      props.actions.showUpcomingMatches();
    }
  }, [props.teams.hasLoaded, props.dashboard.hideUpcomingMatches]);

  const renderPaymentTabs = (): React.ReactNode => (
    <div className="row">
      <div className="col col-md-12 col-lg-10 col-xl-6">
        <ButtonGroup className="tabs w-100">
          <Button noLoading disabled={matchCards.upcoming.length === 0} className={classnames({ active: paymentStatus === 'upcoming' }, 'btn-2')} onClick={() => setPaymentStatus('upcoming')}>
            Upcoming (
            {matchCards.upcoming.length}
            )
          </Button>
          <Button noLoading disabled={matchCards.overdue.length === 0} className={classnames({ active: paymentStatus === 'overdue' }, 'btn-error')} onClick={() => setPaymentStatus('overdue')}>
            Overdue (
            {matchCards.overdue.length}
            )
          </Button>
          <Button noLoading disabled={matchCards.paid.length === 0} className={classnames({ active: paymentStatus === 'paid' }, 'btn-success')} onClick={() => setPaymentStatus('paid')}>
            Paid (
            {matchCards.paid.length}
            )
          </Button>
        </ButtonGroup>
      </div>
    </div>
  );

  const renderPaymentCards = (): React.ReactNode => {
    if (matchCards[paymentStatus] !== undefined) {
      return (
        <div>
          <div className="row">
            <div className={classnames('col payment-card-min-container taller', { taller: paymentStatus === 'paid' }, { 'd-none': matchCards[paymentStatus].length === 0 })}>
              {_.map(
                matchCards[paymentStatus].slice(0, numberOfCards),
                (paymentEventObject, key) => {
                  // console.log(paymentEventObject.paymentEvent.id);
                  // console.log(matchCards[paymentStatus][0]);
                  const mainButton = paymentEventObject.paymentEvent.id === matchCards[paymentStatus][0]?.paymentEvent?.id;
                  const paymentCardData = paymentEventsSelectors.getPaymentEventForMatchCard(
                    paymentEventObject.paymentEvent,
                    paymentEventObject.groupId,
                  );
                  return (
                    <PaymentCardMin
                      {...paymentCardData}
                      key={key}
                      mainButton={mainButton}
                      teamPaymentRequest={_.find(props.teamPaymentRequests.teamPaymentRequestsById, (p) => (!_.isEmpty(p.matchPayment) && p.matchPayment?.id === paymentCardData.matchPaymentId) || (!_.isEmpty(p.forfeit) && p.forfeit?.id === paymentCardData.forfeitId))}
                    />
                  );
                },
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const hasMultipleTeams = (): boolean => props.multipleTeams && props.teams.hasLoaded && props.teams.teamIds?.length > 1;

  const matchesLoaded: boolean = (props.paymentEvents.hasLoaded && !_.isEmpty(props.paymentEvents.paymentEventsByIdSortedDate));
  const matchesEmpty: boolean = (props.paymentEvents.hasLoaded && _.isEmpty(props.paymentEvents.paymentEventsByIdSortedDate));

  const hideNoConnected: boolean = !noConnectedTeams() || props.dashboard.hideUpcomingMatches || !connectionNotification;
  const hideNoActive: boolean = noConnectedTeams() || (!noActiveTeams() || props.dashboard.hideUpcomingMatches || !connectionNotification);
  return (
    <SectionGroup className={classnames({ 'd-none': ((!props.teams.hasLoaded || noConnectedTeams()) && props.dashboard.hideUpcomingMatches) }, { 'with-margin-bottom': !(noConnectedTeams() && props.dashboard.hideUpcomingMatches) })} isLoading={props.paymentEvents.isLoading && _.isEmpty(props.paymentEvents.paymentEventIds)} title="Match Payments" noBorder>
      <div>
        {renderPaymentTabs()}
      </div>
      <div className={classnames({ 'd-none': matchesEmpty })}>
        {renderPaymentCards()}
      </div>
      <div className={classnames({ 'd-none': matchCards[paymentStatus]?.length > 0 })}>
        <div className="">
          No
          {' '}
          {paymentStatus}
          {' '}
          matches.
        </div>
      </div>
      <div className={classnames('with-margin-top', { 'd-none': hideNoConnected })}>
        <Notification
          handleClose={() => {
            props.actions.hideUpcomingMatches();
            setConnectionNotification(false);
          }}
          onClick={(): any => history.push('/connect-team')}
        >
          <div className="d-inline-block">
            {!hasMultipleTeams() && (<h4>Your team is not connected to an association</h4>)}
            {hasMultipleTeams() && (<h4>None of your teams are connected to an association</h4>)}
            <span className="with-margin-top">
              Connect to your association so you can manage payments
              &amp; submit forfeits.
              {' '}
              <Link className="primary" to="/connect-team">
                Connect now &gt;
              </Link>
            </span>
          </div>
        </Notification>
      </div>
      <div className={classnames('with-margin-top', { 'd-none': hideNoActive })}>
        <Notification
          handleClose={() => {
            props.actions.hideUpcomingMatches();
            setConnectionNotification(false);
          }}
          onClick={(): any => history.push('/connect-team')}
        >
          <div className="d-inline-block">
            {!hasMultipleTeams() && (<h4>Your team is not connected to an active season</h4>)}
            {hasMultipleTeams() && (<h4>None of your teams are connected to an active season</h4>)}
            <span className="with-margin-top">
              Connect to your new season so you can see the latest matches.
              {' '}
              <Link className="primary" to="/connect-team">
                Connect now &gt;
              </Link>
            </span>
          </div>
        </Notification>
      </div>
      <div>
        <div className="row with-margin-top-10">
          <div className="col text-right">
            <div className="d-inline-block">
              <Link className="btn-2 btn-thin d-flex align-content-center flex-row" to="/list-payments">
                <span className="d-inline-block">View all matches</span>
                {' '}
                <Isvg className="right-arrow-icon d-inline-block" src={ArrowRightIcon} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </SectionGroup>
  );
};

export default connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))(DashboardUpcomingMatches);
